// Live view: the running match over the WebSocket, rendered with the replay's
// transcript code. Falls back to polling every 10 s while the socket is down.

import { modelLogo } from './lab-logos';
import type { MatchDetailResponse, StatusResponse } from '../src/api/contract';
import type { EndReason, LiveEvent, LiveMatchHeader, RunnerStatus, TranscriptEntry, Winner } from '../src/types';
import { api, describeError, matchUrl, modelUrl, promptUrl } from './api';
import { matchRows } from './cards';
import { emptyState, fact, h, link, prefersReducedMotion, requireEl, show, timeEl } from './dom';
import {
  attackerName,
  endReasonText,
  fmtCountdown,
  fmtInt,
  fmtUtc,
  isExcludedReason,
  modeLabel,
  parseDate,
  phaseText,
  setupLabel,
  stageName,
  truncate,
  winnerText,
} from './format';
import { LiveSocket, type SocketState } from './live';
import { progressOf, StageBar, stageLegend, type StageProgress } from './stagebar';
import { markFromHit, marksFromEvents, TranscriptView } from './transcript';

const POLL_MS = 10_000;

interface Current {
  id: string;
  aId: string;
  bId: string;
  aName: string | null;
  bName: string | null;
  mode: string;
  setup: string;
  promptVersion: string;
  startedAt: string | null;
  turn: number;
  ended: boolean;
  /** False until the backfill from /api/matches/:id has been applied (or given up on). */
  loaded: boolean;
  buffer: LiveEvent[];
  view: TranscriptView;
  bar: StageBar;
  progress: StageProgress;
  turnEl: HTMLElement;
}

export async function initLive(): Promise<void> {
  const page = new LivePage();
  await page.start();
}

class LivePage {
  private readonly conn = requireEl('live-conn');
  private readonly headerBox = requireEl('live-header');
  private readonly stagesBox = requireEl('live-stages');
  private readonly outcomeBox = requireEl('live-outcome');
  private readonly transcriptBox = requireEl('live-transcript');
  private readonly upNextBox = requireEl('live-upnext');
  private readonly announcer = requireEl('live-announcer');
  private readonly jump = requireEl<HTMLButtonElement>('live-jump');

  private cur: Current | null = null;
  private status: StatusResponse | null = null;
  private runner: RunnerStatus | null = null;
  private readonly names = new Map<string, string>();
  private socket: LiveSocket | null = null;
  private socketOpen = false;
  private pollTimer: number | undefined;
  private nextEl: HTMLElement | null = null;
  /** The runner's state under "No match running" (quotas and pacing are on the admin page only). */
  private idleNote: HTMLElement | null = null;
  private idleShown = false;
  private statusFailed = false;
  /** Matches already shown as ended; stale status or late events must not reopen them. */
  private readonly finished = new Set<string>();
  /** "Also playing now": the other live matches' models, fetched once per match. */
  private readonly alsoBox = h('p', { class: 'muted also-live' });
  private readonly others = new Map<string, { a: string; b: string } | null>();

  async start(): Promise<void> {
    this.jump.addEventListener('click', () => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      this.jump.hidden = true;
    });
    window.addEventListener(
      'scroll',
      () => {
        if (!this.jump.hidden && this.nearBottom()) this.jump.hidden = true;
      },
      { passive: true },
    );
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      if (this.pollTimer !== undefined) void this.pollOnce();
      this.socket?.reconnectNow();
    });
    // Back/forward cache: close the socket on the way out, reconnect and catch up on return.
    window.addEventListener('pagehide', () => {
      this.socket?.stop();
      this.socketOpen = false;
    });
    window.addEventListener('pageshow', (ev) => {
      if (!ev.persisted) return;
      this.socket?.start();
      void this.refreshStatus();
      const cur = this.cur;
      if (cur && cur.loaded && !cur.ended) void this.refreshMatch(cur);
    });
    window.setInterval(() => {
      if (!document.hidden && this.nextEl && this.runner?.nextActionAt) this.nextEl.textContent = fmtCountdown(this.runner.nextActionAt);
    }, 1000);

    show(this.transcriptBox, h('p', { class: 'state state--loading' }, 'Connecting to the arena…'));

    void api
      .models()
      .then((res) => {
        for (const m of Array.isArray(res.models) ? res.models : []) if (m && typeof m.id === 'string') this.names.set(m.id, m.displayName || m.id);
        this.renderUpNext();
        if (this.cur) this.renderHeader(this.cur);
      })
      .catch(() => undefined);

    this.socket = new LiveSocket({ onEvent: (ev) => this.onEvent(ev), onState: (s) => this.onSocketState(s) });
    this.socket.start();
    await this.refreshStatus();
  }

  // -------------------------------------------------------------------------
  // Socket and polling

  private onSocketState(s: SocketState): void {
    const dot = (cls: string): HTMLElement => h('span', { class: `conn-dot ${cls}`, attrs: { 'aria-hidden': 'true' } });
    switch (s.kind) {
      case 'open': {
        const reconnected = this.pollTimer !== undefined;
        this.socketOpen = true;
        this.stopPolling();
        show(this.conn, dot('conn-dot--on'), 'Live: connected');
        // Events sent while we were away are not replayed: catch up from the API.
        const cur = this.cur;
        if (reconnected && cur && cur.loaded && !cur.ended) void this.refreshMatch(cur);
        break;
      }
      case 'connecting':
        if (!this.socketOpen && s.attempt === 0) show(this.conn, dot(''), 'Connecting…');
        break;
      case 'retrying':
        this.socketOpen = false;
        this.startPolling();
        show(this.conn, dot('conn-dot--off'), `Live connection lost. Retrying in ${Math.ceil(s.inMs / 1000)} s; checking every 10 s meanwhile.`);
        break;
      case 'unsupported':
        this.startPolling();
        show(this.conn, dot('conn-dot--off'), 'Live updates are unavailable in this browser; refreshing every 10 s.');
        break;
    }
  }

  private startPolling(): void {
    if (this.pollTimer !== undefined) return;
    this.pollTimer = window.setInterval(() => void this.pollOnce(), POLL_MS);
  }

  private stopPolling(): void {
    window.clearInterval(this.pollTimer);
    this.pollTimer = undefined;
  }

  private async pollOnce(): Promise<void> {
    if (document.hidden) return;
    await this.refreshStatus();
    const cur = this.cur;
    if (cur && !cur.ended && cur.loaded) await this.refreshMatch(cur);
  }

  private async refreshStatus(): Promise<void> {
    try {
      const st = await api.status();
      this.status = st;
      this.statusFailed = false;
      const c = st.current;
      if (c && typeof c.id === 'string' && c.status === 'running' && (!this.cur || this.cur.id !== c.id)) {
        this.startMatch(c.id, {
          id: c.id,
          modelA: c.modelA,
          modelB: c.modelB,
          mode: c.mode,
          setup: c.setup ?? 'office',
          promptVersion: c.promptVersion,
          startedAt: c.startedAt,
        });
      }
      if (c && this.cur && this.cur.id === c.id && (!this.cur.aName || !this.cur.bName)) {
        this.cur.aId ||= c.modelA;
        this.cur.bId ||= c.modelB;
        this.cur.aName ||= c.modelAName || null;
        this.cur.bName ||= c.modelBName || null;
        this.renderHeader(this.cur);
      }
      if (st.runner) this.setRunner(st.runner);
      this.renderUpNext();
      if (!this.cur && !c) void this.showIdle();
    } catch (err) {
      this.statusFailed = true;
      if (!this.status) this.renderUpNext();
      console.warn('Status unavailable:', describeError(err));
      // Don't leave "Connecting…" up: a match the socket reports later replaces this.
      if (!this.cur) void this.showIdle();
    }
  }

  // -------------------------------------------------------------------------
  // Events

  /** The current match for an event, starting it if needed; null while its backfill is pending (event buffered). */
  private route(matchId: string, ev: LiveEvent): Current | null {
    if (typeof matchId !== 'string' || !matchId) return null;
    if (!this.cur || this.cur.id !== matchId) {
      if (this.finished.has(matchId)) return null; // a late event for a match we already closed
      if (!this.follows(matchId)) return null; // another of the matches playing at once
      this.startMatch(matchId, null);
    }
    const cur = this.cur;
    if (!cur) return null;
    if (!cur.loaded) {
      cur.buffer.push(ev);
      return null;
    }
    return cur;
  }

  private onEvent(ev: LiveEvent): void {
    switch (ev.type) {
      case 'status':
        if (ev.status) this.setRunner(ev.status);
        break;
      case 'match_started':
        if (ev.match && typeof ev.match.id === 'string' && this.follows(ev.match.id)) this.startMatch(ev.match.id, ev.match);
        break;
      case 'thinking': {
        const cur = this.route(ev.matchId, ev);
        if (cur && !cur.ended) {
          const side = ev.side === 'defender' ? 'defender' : 'attacker';
          // A buffered or late event whose message the backfill already showed: ignore it.
          if (typeof ev.turn === 'number' && cur.view.isPastThinking(side, ev.turn)) break;
          const follow = this.nearBottom();
          cur.view.setThinking(side);
          if (typeof ev.turn === 'number') this.setTurn(cur, ev.turn);
          if (follow) this.scrollToEnd();
        }
        break;
      }
      case 'message': {
        const cur = this.route(ev.matchId, ev);
        if (!cur) break;
        const follow = this.nearBottom();
        const added = cur.view.add([ev.entry]);
        if (added.length === 0) {
          // Already shown (from the API). A model call still finished, so whatever "thinking"
          // came before this event is over.
          if (ev.entry && typeof ev.entry.seq === 'number' && ev.entry.seq >= cur.view.latestSeq) {
            cur.view.setThinking(null);
            this.thinkingForToolRound(cur);
          }
          break;
        }
        cur.view.setThinking(null);
        for (const e of added) {
          this.setTurn(cur, e.turn);
          this.announceEntry(e);
        }
        this.thinkingForToolRound(cur);
        this.afterAppend(follow);
        break;
      }
      case 'stage': {
        const cur = this.route(ev.matchId, ev);
        const mark = cur ? markFromHit(ev.hit) : null;
        if (!cur || !mark) break;
        cur.view.addMarks([mark]);
        cur.progress.proven.add(mark.stage);
        cur.progress.highestStage = Math.max(cur.progress.highestStage, mark.stage);
        cur.bar.update(cur.progress);
        cur.bar.flash(mark.stage);
        this.announce(`Stage ${mark.stage} proven: ${stageName(mark.stage, cur.setup)}.`);
        break;
      }
      case 'verdict': {
        const cur = this.route(ev.matchId, ev);
        if (!cur || !ev.verdict) break;
        const follow = this.nearBottom();
        // The judges run after both models: any typing indicator still shown is stale.
        cur.view.setThinking(null);
        cur.view.setVerdict(ev.verdict);
        const v = ev.verdict;
        if (v.verbalStatus === 'agreed' && typeof v.verbalStage === 'number' && v.verbalStage > cur.progress.highestVerbalStage) {
          cur.progress.highestVerbalStage = v.verbalStage;
          cur.bar.update(cur.progress);
        }
        if (follow) this.scrollToEnd();
        break;
      }
      case 'match_ended': {
        const cur = this.route(ev.matchId, ev);
        if (!cur) break;
        if (typeof ev.highestStage === 'number') cur.progress.highestStage = Math.max(cur.progress.highestStage, ev.highestStage);
        cur.bar.update(cur.progress);
        this.showEnded(cur, ev.endReason, ev.winner);
        // Refresh "up next" once the scheduler has moved on (the status endpoint is cached ~5 s).
        window.setTimeout(() => void this.refreshStatus(), 6000);
        break;
      }
    }
  }

  /**
   * Several matches can play at once (the runner's liveMatchIds). The page follows the
   * featured one (currentMatchId) and leaves the others' events alone. A runner from
   * before that field had one match, so every event was the match to follow.
   */
  private follows(matchId: string): boolean {
    const r = this.runner;
    if (!r || !Array.isArray(r.liveMatchIds)) return true;
    return r.currentMatchId === matchId;
  }

  /** "Also playing now: A vs B, C vs D", linking to their replay pages. */
  private renderAlso(): void {
    const ids = Array.isArray(this.runner?.liveMatchIds) ? this.runner!.liveMatchIds.filter((id) => typeof id === 'string' && id !== this.cur?.id) : [];
    for (const id of ids) {
      if (this.others.has(id)) continue;
      this.others.set(id, null);
      void api
        .match(id)
        .then((d) => {
          this.others.set(id, { a: d.match.modelAName || this.name(d.match.modelA), b: d.match.modelBName || this.name(d.match.modelB) });
          this.renderAlso();
        })
        .catch(() => this.others.delete(id));
    }
    if (!ids.length) {
      this.alsoBox.replaceChildren();
      return;
    }
    const parts = ids.flatMap((id, i) => {
      const o = this.others.get(id);
      return [i ? ', ' : '', link(matchUrl(id), o ? `${o.a} vs ${o.b}` : 'another match')];
    });
    this.alsoBox.replaceChildren('Also playing now: ', ...parts);
  }

  private setRunner(r: RunnerStatus): void {
    this.runner = r;
    this.renderIdleNote();
    this.renderAlso();
    const cur = this.cur;
    if (r.currentMatchId && (!cur || cur.id !== r.currentMatchId)) {
      this.startMatch(r.currentMatchId, null);
    } else if (cur && !cur.ended && cur.loaded && r.currentMatchId !== cur.id) {
      // Our match is no longer current but we missed its end: fetch the final state.
      void this.refreshMatch(cur);
    } else if (!r.currentMatchId && !cur) {
      // Nothing running (also covers a socket that is up while /api/status failed).
      void this.showIdle();
    }
    if (this.cur && r.currentMatchId === this.cur.id && typeof r.turn === 'number') this.setTurn(this.cur, r.turn);
  }

  // -------------------------------------------------------------------------
  // Match lifecycle

  private startMatch(id: string, header: LiveMatchHeader | null): void {
    if (this.cur && this.cur.id === id) return;
    if (this.finished.has(id) && this.cur) return;
    this.idleShown = false;
    const view = new TranscriptView(id, { emptyText: 'Waiting for the first message…', attacker: attackerName(header?.setup), setup: header?.setup });
    const bar = new StageBar({ setup: header?.setup });
    const cur: Current = {
      id,
      aId: header?.modelA ?? '',
      bId: header?.modelB ?? '',
      aName: null,
      bName: null,
      mode: header?.mode ?? '',
      setup: header?.setup ?? 'office',
      promptVersion: header?.promptVersion ?? '',
      startedAt: header?.startedAt ?? null,
      turn: 0,
      ended: false,
      loaded: false,
      buffer: [],
      view,
      bar,
      progress: { proven: new Set(), highestStage: 0, highestVerbalStage: 0 },
      turnEl: h('span', null, '—'),
    };
    this.cur = cur;
    this.jump.hidden = true;
    this.renderHeader(cur);
    show(this.stagesBox, bar.el, stageLegend());
    show(this.outcomeBox);
    show(this.transcriptBox, view.el);
    // Also sent for a match already running when the page connects, so not "new match".
    if (header) this.announce(`Now playing: ${this.name(cur.aId)} attacking ${this.name(cur.bId)}.`);
    void this.backfill(cur, 0);
  }

  private async backfill(cur: Current, attempt: number): Promise<void> {
    let ok = false;
    try {
      const data = await api.match(cur.id);
      if (this.cur !== cur) return;
      this.applyDetail(cur, data);
      ok = true;
    } catch (err) {
      if (this.cur !== cur) return;
      if (attempt >= 3) {
        show(this.outcomeBox, h('p', { class: 'state state--error' }, `Earlier messages could not be loaded: ${describeError(err)}`));
      }
    }
    if (!cur.loaded) {
      // Live events that arrived meanwhile are replayed now; duplicates are dropped by seq.
      cur.loaded = true;
      const buffered = cur.buffer;
      cur.buffer = [];
      for (const ev of buffered) this.onEvent(ev);
      this.thinkingForToolRound(cur);
    }
    if (!ok && attempt < 3) window.setTimeout(() => void this.backfill(cur, attempt + 1), 4000 * (attempt + 1));
    // The match endpoint is edge-cached for a few seconds: fetch once more to fill any gap.
    if (ok && attempt === 0) window.setTimeout(() => void this.refreshMatch(cur), 6000);
  }

  private async refreshMatch(cur: Current): Promise<void> {
    try {
      const follow = this.nearBottom();
      const data = await api.match(cur.id);
      if (this.cur !== cur) return;
      const added = this.applyDetail(cur, data);
      const last = added.at(-1);
      if (last) {
        cur.view.setThinking(null);
        this.thinkingForToolRound(cur);
        this.announceEntry(last);
        this.afterAppend(follow);
      }
    } catch {
      // The next poll or event retries.
    }
  }

  /** Alex answered tool calls and the runner will call it again: show it as thinking (no event says so). */
  private thinkingForToolRound(cur: Current): void {
    if (!cur.ended && cur.view.awaitingToolRound()) cur.view.setThinking('defender');
  }

  private applyDetail(cur: Current, data: MatchDetailResponse): TranscriptEntry[] {
    const m = data.match;
    if (!m || m.id !== cur.id) return [];
    cur.aId = m.modelA || cur.aId;
    cur.bId = m.modelB || cur.bId;
    cur.aName = m.modelAName || cur.aName;
    cur.bName = m.modelBName || cur.bName;
    cur.mode = m.mode || cur.mode;
    cur.setup = m.setup || cur.setup;
    cur.promptVersion = m.promptVersion || cur.promptVersion;
    cur.startedAt = m.startedAt || cur.startedAt;
    this.renderHeader(cur);

    const added = cur.view.add(Array.isArray(data.messages) ? data.messages : []);
    cur.view.setVerdicts(data.verdicts);
    cur.view.setJudgements(data.judgements);
    cur.view.addMarks(marksFromEvents(data.stages));

    const p = progressOf(m);
    for (const s of cur.progress.proven) p.proven.add(s);
    p.highestStage = Math.max(p.highestStage, cur.progress.highestStage);
    p.highestVerbalStage = Math.max(p.highestVerbalStage, cur.progress.highestVerbalStage);
    cur.progress = p;
    cur.bar.update(p);
    this.setTurn(cur, m.totalTurns);

    if (m.status !== 'running' && !cur.ended) this.showEnded(cur, m.endReason, m.winner);
    return added;
  }

  private showEnded(cur: Current, reason: EndReason | null, winner: Winner): void {
    if (cur.ended) return;
    const follow = this.nearBottom();
    cur.ended = true;
    this.finished.add(cur.id);
    cur.view.setThinking(null);
    cur.view.settle();
    const cls = winner === 'attacker' ? 'outcome--atk' : winner === 'defender' ? 'outcome--def' : isExcludedReason(reason) ? 'outcome--void' : 'outcome--draw';
    show(
      this.outcomeBox,
      h(
        'div',
        { class: `outcome-card ${cls}` },
        h('p', { class: 'outcome-title' }, `Match over: ${winnerText(winner)}`),
        h('p', null, `${endReasonText(reason, cur.setup)}. Stages proven: ${cur.progress.proven.size} of 6.`),
        h('p', null, link(matchUrl(cur.id), 'Open the replay →')),
        h('p', { class: 'muted' }, 'Waiting for the next match…'),
      ),
    );
    this.renderHeader(cur);
    this.announce(`Match over. ${winnerText(winner)}: ${endReasonText(reason, cur.setup)}.`);
    if (follow && cur.loaded) this.scrollToEnd();
  }

  /** Nothing running: say so, and offer the latest finished match. */
  private async showIdle(): Promise<void> {
    if (this.idleShown || this.cur) return;
    this.idleShown = true;
    show(this.headerBox, h('h2', { class: 'vs-title' }, 'No match running right now'));
    show(this.stagesBox);
    show(this.outcomeBox);
    const latest = h('div');
    this.idleNote = h('p', { class: 'idle-note muted' });
    this.renderIdleNote();
    show(
      this.transcriptBox,
      emptyState('Waiting for the next match', 'This page updates by itself when the next match starts.'),
      this.idleNote,
      latest,
    );
    try {
      const res = await api.matches({ limit: 1 });
      const m = Array.isArray(res.matches) ? res.matches[0] : undefined;
      if (m && !this.cur) show(latest, h('h3', { class: 'subhead' }, 'Most recent match'), matchRows([m]));
    } catch {
      // Optional extra; ignore.
    }
  }

  // -------------------------------------------------------------------------
  // Rendering

  private name(id: string): string {
    return this.names.get(id) ?? id;
  }

  private setTurn(cur: Current, turn: number): void {
    if (typeof turn !== 'number' || !Number.isFinite(turn) || turn <= cur.turn) return;
    cur.turn = turn;
    cur.turnEl.textContent = fmtInt(turn);
  }

  private renderHeader(cur: Current): void {
    const aName = cur.aName || (cur.aId ? this.name(cur.aId) : 'Attacker');
    const bName = cur.bName || (cur.bId ? this.name(cur.bId) : 'Defender');
    cur.turnEl.textContent = cur.turn > 0 ? fmtInt(cur.turn) : '—';
    show(
      this.headerBox,
      h(
        'p',
        { class: 'eyebrow' },
        cur.ended ? null : h('span', { class: 'live-dot', attrs: { 'aria-hidden': 'true' } }),
        cur.ended ? 'Just finished' : 'Now playing',
      ),
      h(
        'h2',
        { class: 'vs-title' },
        cur.aId ? h('span', { class: 'who' }, modelLogo(cur.aId, 22), link(modelUrl(cur.aId), aName, 'atk')) : h('span', { class: 'atk' }, aName),
        h('span', { class: 'vs' }, ' vs '),
        cur.bId ? h('span', { class: 'who' }, modelLogo(cur.bId, 22), link(modelUrl(cur.bId), bName, 'def')) : h('span', { class: 'def' }, bName),
      ),
      h('p', { class: 'roles' }, h('span', { class: 'atk' }, `Attacker plays ${attackerName(cur.setup)}`), h('span', { class: 'def' }, 'Defender plays Alex')),
      h(
        'dl',
        { class: 'facts' },
        fact('Setup', setupLabel(cur.setup)),
        fact('Mode', cur.mode ? modeLabel(cur.mode) : '—'),
        fact('Turn', cur.turnEl),
        fact('Started', cur.startedAt ? timeEl(cur.startedAt) : '—'),
        fact('Prompt version', cur.promptVersion ? link(promptUrl(cur.promptVersion), cur.promptVersion, 'mono') : '—'),
      ),
      h('p', null, link(matchUrl(cur.id), 'Replay page for this match →')),
      this.alsoBox,
    );
    this.renderAlso();
  }

  /** What the runner is doing while no match runs: paused, waiting, between matches. */
  private renderIdleNote(): void {
    const el = this.idleNote;
    if (!el) return;
    const r = this.runner;
    this.nextEl = null;
    if (!r || this.cur) {
      el.replaceChildren();
      return;
    }
    const next = parseDate(r.nextActionAt);
    if (next && r.nextActionAt && next.getTime() > Date.now() + 1000) {
      this.nextEl = h('span', { attrs: { title: fmtUtc(r.nextActionAt) } }, fmtCountdown(r.nextActionAt));
    }
    el.replaceChildren(
      `Arena: ${phaseText(r.phase)}`,
      r.detail ? ` · ${r.detail}` : '',
      ...(this.nextEl ? [' · next step ', this.nextEl] : []),
    );
  }

  private renderUpNext(): void {
    const list = Array.isArray(this.status?.upNext) ? (this.status?.upNext ?? []) : [];
    if (list.length === 0) {
      const text = this.status
        ? 'Nothing queued yet. The scheduler picks the next pairing when a match ends.'
        : this.statusFailed
          ? "The queue couldn't be loaded right now."
          : 'Loading…';
      show(this.upNextBox, h('p', { class: 'muted' }, text));
      return;
    }
    show(
      this.upNextBox,
      h(
        'ol',
        { class: 'upnext' },
        ...list.slice(0, 8).map((q) =>
          h(
            'li',
            null,
            h('span', { class: 'who' }, modelLogo(q.modelA, 14), link(modelUrl(q.modelA), this.name(q.modelA), 'atk')),
            h('span', { class: 'vs' }, ' vs '),
            h('span', { class: 'who' }, modelLogo(q.modelB, 14), link(modelUrl(q.modelB), this.name(q.modelB), 'def')),
            h('span', { class: 'tag' }, setupLabel(q.setup ?? 'office')),
            h('span', { class: 'tag' }, modeLabel(q.mode)),
          ),
        ),
      ),
    );
  }

  // -------------------------------------------------------------------------
  // Scrolling and announcements

  private nearBottom(): boolean {
    const doc = document.documentElement;
    return window.innerHeight + window.scrollY >= doc.scrollHeight - 200;
  }

  private scrollToEnd(): void {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  private afterAppend(follow: boolean): void {
    if (follow) this.scrollToEnd();
    else this.jump.hidden = false;
  }

  /** Adds a line to the polite live region (screen readers read additions; old lines are trimmed). */
  private announce(text: string): void {
    this.announcer.append(h('p', null, text));
    while (this.announcer.childElementCount > 5) this.announcer.firstElementChild?.remove();
  }

  private announceEntry(e: TranscriptEntry): void {
    const text = typeof e.content === 'string' ? e.content : '';
    if (e.kind === 'attacker') this.announce(`${attackerName(this.cur?.setup)}: ${truncate(text, 240)}`);
    else if (e.kind === 'defender') {
      const calls = Array.isArray(e.toolCalls) ? e.toolCalls.map((c) => c?.function?.name).filter((n): n is string => typeof n === 'string') : [];
      const said = text.trim() ? `Alex: ${truncate(text, 240)}` : 'Alex';
      this.announce(calls.length ? `${said} (used ${calls.join(', ')})` : said);
    }
  }
}
